import type { LocaleCode } from '~/types'
import { blockedDomains } from './blockedDomains'

/**
 * Apply custom order for country select options
 * Depending on the locale it will show the configured `orderPerLocale` options at the beginning of the list
 *
 * @param {Object[]} data - Marketo form field select options list
 * @param {String} locale - i18n locale
 * @returns {Object[]} - ordered select options list
 */
export const countryCustomOrder = (data: { text: string; value: string }[], locale: LocaleCode) => {
  const orderPerLocale: Record<LocaleCode, string[]> = {
    en: ['US', 'DE', 'NL', 'GB', 'FR'],
    pt_BR: ['BR'],
    zh_CN: ['CN', 'HK'],
    cs_CZ: ['CZ'],
    da_DK: ['DK'],
    de_DE: ['DE', 'AT', 'CH'],
    es_ES: ['ES'],
    fr_FR: ['FR'],
    it_IT: ['IT'],
    ja_JP: ['JP'],
    es_MX: ['MX'],
    nl_NL: ['NL', 'BE'],
    no_NO: ['NO'],
    pt_PT: ['PT'],
    sv_SE: ['SE'],
    pl_PL: ['PL'],
    en_GB: ['GB', 'IE', 'IM'],
    en_AU: ['AU'],
    en_SG: ['SG'],
    en_AE: []
  }

  if (!data || !Array.isArray(data)) {
    return data
  }

  // order alphabetically a-z
  // Warning! array mutation
  data.sort((a, b) => a.text.localeCompare(b.text))

  if (!orderPerLocale[locale]?.length) {
    return data
  }

  const reversedOrder = orderPerLocale[locale].reverse()
  // apply custom order
  // Warning! array mutation
  data.sort((a, b) => reversedOrder.indexOf(b.value) - reversedOrder.indexOf(a.value))

  return data
}

/**
 * Validate email address without restricting the domain
 * @param {String} value - email address
 * @returns {Boolean} validation result
 */
export const validateGenericEmail = (value?: string): boolean => {
  if (!value) return false
  const regex = new RegExp(
    ['^([\\w+.-]+)@(\\[(\\d{1,3}\\.){3}|', '(([a-zA-Z\\d-]+\\.)+))([a-zA-Z]{2,63}|\\d{1,3})(\\]?)$'].join('')
  )
  return regex.test(value)
}

/**
 * Email field validation with restricting the domain
 * @param {String} value - field value
 * @return {Boolean} - is valid when the value has an email address format a-zA-Z0-9@._+-
 */
const emailField = (value: string): boolean => {
  return validateGenericEmail(value) && isValidDomain(value)
}

/**
 * Checks given email address and against the list of blocked domains names
 * @returns {*|boolean} true if the domain is not in the blocked domains list
 */
const isValidDomain = (email: string): boolean => {
  const domain = getDomain(email)
  return !!domain && !blockedDomains.includes(domain)
}

/**
 * Gets domain name from the given email address
 * examples:
 * a@example.com => example
 * a@mercedes-benz.com => mercedes-benz
 * a@yahoo.co.uk => yahoo
 */
const getDomain = (email: string): string | undefined => email?.split('@')?.[1]?.split('.')?.[0]

// TODO: Replace unicode character ranges with patterns e.g `\p{Script=Hiragana}` or `\p{Script=Han}`
/**
 * Name field validation
 * @return {Boolean} - is valid when the value has alpha characters only, min 1 - max 30
 */
const nameField = (value: string): boolean => {
  // `[\u00C0-\u00FF]` Matches a character in the range À to ÿ (char code 192 to 255)
  // `[\u3000-\u303F\u3400-\u4DBF\u4E00-\u9FFF]` Matches Chinese characters
  // `[\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF]` Matches Japanese characters
  return /^[a-zA-Z\u00C0-\u00FF\u3000-\u303F\u3400-\u4DBF\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF ]{1,30}$/.test(
    value
  )
}

/**
 * Company name field validation
 * @return {Boolean} - is valid when the value has alphaNumeric characters only, min 1 - max 30
 */
const companyNameField = (value: string): boolean => {
  // `[\u00C0-\u00FF]` Matches a character in the range À to ÿ (char code 192 to 255)
  // `[\u3000-\u303F\u3400-\u4DBF\u4E00-\u9FFF]` Matches Chinese characters
  // `[\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF]` Matches Japanese characters
  return /^[A-Za-z\u00C0-\u00FF\u3000-\u303F\u3400-\u4DBF\u4E00-\u9FFF\u3040-\u309F\u30A0-\u30FF\uFF00-\uFFEF\u4E00-\u9FAF\d ]{1,30}$/.test(
    value
  )
}

/**
 * Phone number field validation
 * @return {Boolean} - is valid when the value has digits only, min 7 - max 15
 */
const phoneNumberField = (value: string): boolean => {
  return /^\+?\d{7,15}$/.test(value)
}

/**
 * Website field validation
 * @return {Boolean} - is valid when the value is https://xxx.xxx.xxx
 */
const websiteField = (value: string): boolean => {
  // eslint-disable-next-line no-useless-escape
  return /^[https:\/\/]{8}(?!\.)(?!.*\.$)(?!.*\.\.)(?!(?:.*?\.){3})[-.a-z0-9]{2,35}\.[a-z]{2,6}$/.test(value)
}

export const formValidationRules = { phoneNumberField, companyNameField, nameField, emailField, websiteField }

// basic mapping per field type
const FIELDS = {
  email: {
    type: 'email',
    label: 'formFieldEmail',
    errorMessage: 'formFieldEmailErrorMessage',
    validate: formValidationRules.emailField,
    required: false,
    infoMessage: '',
    maxLength: '70'
  },
  telephone: {
    type: 'tel',
    label: 'formFieldPhoneNumber',
    errorMessage: 'formFieldPhoneNumberErrorMessage',
    validate: formValidationRules.phoneNumberField,
    required: false,
    initialValue: '+',
    infoMessage: ''
  },
  text: {
    type: 'text',
    label: '',
    errorMessage: 'formFieldNameErrorMessage',
    validate: formValidationRules.nameField,
    required: false,
    infoMessage: ''
  },
  url: {
    type: 'url',
    label: 'formFieldWebsite',
    errorMessage: 'formFieldWebsiteErrorMessage',
    // Use a more generic validation rule to allow urls with subdomain and 2 top level domains
    // @TODO: review if we want to move this validation to Design System
    validate: (value: string) => {
      return /^https:\/\/(?:[-a-z0-9]{2,35}\.)?(?:[-a-z0-9]{2,35})(?:\.[a-z]{2,6}){1,2}$/.test(value)
    },
    required: false,
    initialValue: 'https://',
    infoMessage: '',
    maxLength: '70'
  },
  checkbox: {
    type: 'checkbox',
    label: '',
    errorMessage: '',
    validate: null,
    required: false,
    infoMessage: ''
  },
  checkboxes: {
    type: 'checkbox',
    label: '',
    errorMessage: '',
    validate: null,
    required: false,
    infoMessage: ''
  },
  select: {
    type: 'select',
    label: '',
    errorMessage: '',
    validate: null,
    required: false,
    infoMessage: '',
    placeholder: '',
    options: []
  },
  textarea: {
    type: 'textarea',
    label: 'formFieldMessage',
    errorMessage: '',
    validate: null,
    required: false,
    infoMessage: ''
  }
}

// mapping per field id
// Note: there are inconsistences in the field id's in the api response vs form submit payload
// e.g the id is 'FirstName' but we have to send it back as 'firstName'
export const SUPPORTED_FIELDS = {
  FirstName: {
    ...FIELDS.text,
    name: 'firstName',
    label: 'formFieldFirstName'
  },
  LastName: {
    ...FIELDS.text,
    name: 'lastName',
    label: 'formFieldLastName'
  },
  Company: {
    ...FIELDS.text,
    name: 'company',
    label: 'formFieldCompany',
    errorMessage: 'formFieldCompanyNameErrorMessage',
    validate: formValidationRules.companyNameField
  },
  Phone: {
    ...FIELDS.telephone,
    name: 'phone'
  },
  Website: {
    ...FIELDS.url,
    name: 'website',
    infoMessage: 'formFieldWebsiteInfoMessage'
  },
  Email: {
    ...FIELDS.email,
    name: 'email'
  },
  corporateEmailAddress: {
    ...FIELDS.email,
    name: 'corporateEmailAddress',
    label: 'formFieldCorporateEmail'
  },
  Consented_to_Contact__c: {
    ...FIELDS.checkboxes,
    label: 'formCheckboxConsentedToContact',
    errorMessage: 'formFieldRequiredErrorMessage'
  },
  PartnerNewsletter__c: {
    ...FIELDS.checkboxes,
    label: 'formCheckboxConsentedToContact',
    errorMessage: 'formFieldRequiredErrorMessage'
  },
  pressoptin: {
    ...FIELDS.checkboxes,
    label: 'formCheckboxConsentedToContact',
    errorMessage: 'formFieldRequiredErrorMessage'
  },
  CountryCode: {
    ...FIELDS.select,
    label: 'formFieldCountry',
    placeholder: 'formFieldCountryPlaceholder',
    infoMessage: 'formFieldCountryInfoMessage',
    options: 'countryOptions',
    translationKey: 'countries',
    order: countryCustomOrder
  },
  State: {
    ...FIELDS.select,
    name: 'state',
    label: 'formFieldStateProvinceTerritory',
    translationKey: 'states'
  },
  Industry: {
    ...FIELDS.select,
    name: 'industry',
    label: 'formFieldIndustry',
    translationKey: 'industry'
  },
  Form_text_2__c: {
    ...FIELDS.textarea,
    name: 'Form_text_2__c'
  },
  EstimatedATV__c: {
    type: 'number',
    name: 'EstimatedATV__c',
    label: 'formFieldEstimatedAtv'
  },
  NumberOfTransactionsAMonth__c: {
    type: 'number',
    name: 'NumberOfTransactionsAMonth__c',
    label: 'formFieldTransactionsAMonth',
    errorMessage: 'formFieldTransactionsAMonthErrorMessage',
    // Custom max validation to match Marketo max allowed number
    validate: (value: string | number) => {
      return Number(value) < 2147483647
    }
  },
  ECOMMERCE_PLATFORM__c: {
    ...FIELDS.select,
    label: 'formFieldEcommercePlatform',
    infoMessage: 'formSelectAll',
    // Options partically translated
    translationKey: 'ecommercePlatform'
  },
  POS_Sotfware__c: {
    ...FIELDS.select,
    label: 'formFieldPosSotfware',
    infoMessage: 'formSelectAll'
    // No translations needed for Options
  },
  PartnerChannelV2__c: {
    ...FIELDS.select,
    label: 'formFieldPartnerChannel',
    infoMessage: 'formSelectAll',
    translationKey: 'partnerChannel'
  },
  Company_Segment__c: {
    ...FIELDS.select,
    label: 'formFieldCompanySegment',
    infoMessage: 'formFieldCompanySegmentInfoMessage',
    translationKey: 'companySegments'
  },
  SalesChannelSingle__c: {
    ...FIELDS.select,
    label: 'formFieldSalesChannelSingle',
    translationKey: 'salesChannelSingle'
  },
  Product_Needs__c: {
    ...FIELDS.select,
    label: 'formFieldProductNeeds',
    translationKey: 'productNeeds',
    errorMessage: 'formFieldRequiredErrorMessage'
  },
  PartnerTypes__c: {
    ...FIELDS.select,
    label: 'formFieldPartnerTypes',
    translationKey: 'partnerTypes'
  },
  platformactiveusers: {
    ...FIELDS.select,
    label: 'formFieldPlatformActiveUsers',
    translationKey: 'platformActiveUsers'
  },
  referredby: {
    type: 'text',
    name: 'referredby',
    label: 'formFieldReferredBy'
  },
  Preferred_Language__c: {
    ...FIELDS.select,
    name: 'Preferred_Language__c',
    label: 'formFieldPreferredLanguage',
    translationKey: 'preferredLanguage'
  },
  Department__c: {
    ...FIELDS.select,
    name: 'Department__c',
    label: 'formFieldDepartment',
    translationKey: 'department'
  },
  Title: {
    type: 'text',
    name: 'title',
    label: 'formFieldJobTitle'
  },
  LinkedIn_Profile__c: {
    ...FIELDS.url,
    name: 'LinkedIn_Profile__c',
    label: 'formFieldLinkedInProfile',
    errorMessage: 'formFieldLinkedInProfileErrorMessage',
    maxLength: '150',
    // Custom url validation
    validate: (value: string) => {
      return /^(https:\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&\/=]*)$/.test(value)
    }
  },
  ServiceDescription__c: {
    ...FIELDS.checkboxes,
    name: 'ServiceDescription__c',
    label: 'formFieldServiceDescription'
  },
  SalesPayoutDay__c: {
    ...FIELDS.checkboxes,
    name: 'SalesPayoutDay__c',
    label: 'formFieldSalesPayoutDay'
  },
  afPSalesDayPayoutOptIn: {
    ...FIELDS.checkboxes,
    name: 'afPSalesDayPayoutOptIn',
    label: 'formFieldAfPSalesDayPayout'
  }
}
